import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Loader2,
  Lock,
  ShieldCheck,
} from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { Stepper } from '../../components/Stepper';
import { OptionCard } from '../../components/OptionCard';
import { cn } from '../../lib/cn';

const STEPS = ['Admin password', 'Platform settings', 'Connect SSLCommerz'];

/**
 * First-install bootstrap wizard — §3.2
 * Three steps: set platform admin password, configure platform settings,
 * then connect SSLCommerz.
 */
export function Bootstrap() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  // step 1
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [enable2fa, setEnable2fa] = useState(true);

  // step 2
  const [brandName, setBrandName] = useState('Possignin');
  const [currency, setCurrency] = useState('BDT');
  const [timezone, setTimezone] = useState('Asia/Dhaka');
  const [supportEmail, setSupportEmail] = useState('support@itcitysoft.com');
  const [supportPhone, setSupportPhone] = useState('+8801717540035');

  // step 3
  const [sslMode, setSslMode] = useState<'sandbox' | 'live'>('sandbox');
  const [storeId, setStoreId] = useState('');
  const [storePassword, setStorePassword] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle');
  const [submitting, setSubmitting] = useState(false);

  function next() {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
  }
  function back() {
    if (step > 0) setStep((s) => s - 1);
  }

  function testConnection() {
    if (!storeId || !storePassword) return;
    setTestStatus('testing');
    setTimeout(() => setTestStatus('ok'), 1100);
  }

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;
  const step1Valid = password.length >= 12 && !mismatch;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step === 0 && !step1Valid) return;
    if (step < STEPS.length - 1) {
      next();
      return;
    }
    setSubmitting(true);
    setTimeout(() => navigate({ to: '/admin' }), 1100);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Possignin
          </span>
          <span className="px-1.5 py-px rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            Setup
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
            <Lock className="w-3.5 h-3.5" />
            One-time setup link
          </span>
          <AuthThemeToggle />
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8 sm:py-12 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Welcome to Possignin SaaS
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            Three quick steps to bring up your control plane.
          </p>
        </div>

        <div className="mb-8 flex justify-center">
          <Stepper steps={STEPS} current={step} />
        </div>

        <Card className="p-5 sm:p-8">
          <form onSubmit={onSubmit} className="space-y-6">
            {step === 0 && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                  Set your platform admin password
                </h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                  This is the password for the seed super-admin. Make it long.
                </p>

                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">
                    Email
                  </div>
                  <div className="px-3 py-2.5 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 mono text-sm text-zinc-700 dark:text-zinc-200">
                    sayem@rootsoftit.com
                  </div>
                  <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                    From <code className="mono">POSSIGNIN_BOOTSTRAP_EMAIL</code> env var
                  </p>
                </div>

                <Input
                  label="Password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint="At least 12 characters with letters, numbers, and symbols"
                  error={tooShort ? 'Must be at least 12 characters' : undefined}
                  autoComplete="new-password"
                />
                <Input
                  label="Confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={mismatch ? "Passwords don't match" : undefined}
                  autoComplete="new-password"
                />

                <label className="flex items-start gap-3 p-3 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enable2fa}
                    onChange={(e) => setEnable2fa(e.target.checked)}
                    className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-zinc-950 dark:text-zinc-50 flex-wrap">
                      <ShieldCheck className="w-4 h-4 text-brand dark:text-brand-dm" />
                      Enable 2FA now
                      <span className="px-1.5 py-px rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        Recommended
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                      Required for the platform admin role in production.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                  Platform settings
                </h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                  These defaults apply to every reseller and business unless overridden.
                </p>

                <Input
                  label="Brand name"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                />
                <div className="grid sm:grid-cols-2 gap-5">
                  <SelectField
                    label="Default currency"
                    value={currency}
                    onChange={setCurrency}
                    options={['BDT', 'USD', 'EUR']}
                  />
                  <SelectField
                    label="Default timezone"
                    value={timezone}
                    onChange={setTimezone}
                    options={['Asia/Dhaka', 'Asia/Kolkata', 'Asia/Singapore', 'UTC']}
                  />
                </div>
                <Input
                  label="Support email"
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                />
                <Input
                  label="Support phone"
                  value={supportPhone}
                  onChange={(e) => setSupportPhone(e.target.value)}
                />
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                  Connect SSLCommerz
                </h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                  Required for online payments. You can switch from Sandbox to Live later.
                </p>

                <div>
                  <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                    Mode
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <OptionCard
                      selected={sslMode === 'sandbox'}
                      onSelect={() => setSslMode('sandbox')}
                    >
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">Sandbox</div>
                      <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                        Test mode — no real charges.
                      </p>
                    </OptionCard>
                    <OptionCard
                      selected={sslMode === 'live'}
                      onSelect={() => setSslMode('live')}
                    >
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">Live</div>
                      <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                        Production credentials only.
                      </p>
                    </OptionCard>
                  </div>
                </div>

                <Input
                  label="Store ID"
                  required
                  value={storeId}
                  onChange={(e) => {
                    setStoreId(e.target.value);
                    setTestStatus('idle');
                  }}
                  placeholder="possignin_live_001"
                />
                <Input
                  label="Store password"
                  type="password"
                  required
                  value={storePassword}
                  onChange={(e) => {
                    setStorePassword(e.target.value);
                    setTestStatus('idle');
                  }}
                />

                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">
                    IPN URL (read-only)
                  </div>
                  <div className="px-3 py-2.5 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 mono text-xs text-zinc-700 dark:text-zinc-200 break-all">
                    https://saas.itcitysoft.com/webhooks/sslcommerz/ipn
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={testConnection}
                    disabled={!storeId || !storePassword || testStatus === 'testing'}
                    iconLeft={
                      testStatus === 'testing' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : undefined
                    }
                  >
                    {testStatus === 'testing' ? 'Testing…' : 'Test connection'}
                  </Button>
                  {testStatus === 'ok' && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="w-4 h-4" />
                      Verified · 200 OK
                    </span>
                  )}
                  {testStatus === 'fail' && (
                    <span className="text-sm text-rose-600 dark:text-rose-400">
                      Connection failed — check credentials.
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="pt-2 flex items-center justify-between gap-3 border-t border-zinc-200 dark:border-zinc-700 mt-6">
              {step > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={back}
                  iconLeft={<ArrowLeft className="w-4 h-4" />}
                  className="mt-6"
                >
                  Back
                </Button>
              ) : (
                <span />
              )}

              <Button
                type="submit"
                loading={submitting}
                disabled={step === 0 && !step1Valid}
                iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
                className="mt-6"
              >
                {step < STEPS.length - 1 ? 'Continue' : 'Finish setup'}
              </Button>
            </div>
          </form>
        </Card>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Check className="w-3 h-3" />
          One-time setup. After this, the bootstrap link is invalidated.
        </p>
      </main>
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'block w-full px-3 py-2.5 text-sm rounded-md',
          'bg-white text-zinc-950 dark:bg-zinc-900 dark:text-zinc-50',
          'border border-zinc-300 dark:border-zinc-700',
          'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
        )}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
